
import { defineComponent, onBeforeMount, reactive } from 'vue'
import { useRoute, useRouter } from 'vue-router'

import PageState from '@/modules/OktellModule/components/PageState/index.vue'
import OkInvoiceHeader from '@/modules/OktellModule/components/OkInvoiceHeader/index.vue'
import OkInvoiceEdit from '@/modules/OktellModule/components/OkInvoiceEdit/index.vue'

import { InvoiceT } from '@/core/types/orders.types'
import { CompletedCargoT } from '@/core/types/create_order.types'

import { orders } from '@/core/realisations/orders.realisation'

import useNotifications from '@/common/composable/useNotifications'

import { EMPTY_ADDITIONAL_SERVICES } from '@/core/constants/additional_services.constants'
import { ADDITIONAL_SERVICES_KEYS } from '@/core/constants/ltl.constants'
import { AdditionalServicesI, AdditionalServicesLTLI } from '@/core/types/additional_services.types'
import { receivers } from '@/core/realisations/receivers.realisation'

export default defineComponent({
    components: {
        PageState,
        OkInvoiceHeader,
        OkInvoiceEdit,
    },
    setup() {
        const notifications = useNotifications()

        const route = useRoute()
        const router = useRouter()
        const invoice_id = Number(route.params.invoice_id as string)

        const state = reactive({
            is_loading: false,
            is_failed: false,
            is_receiver_services_loading: false,
            invoice: null as InvoiceT | null,
            updating_cargo: null as CompletedCargoT | null,
            additional_services_temp: null as AdditionalServicesLTLI | null,
        })

        const getAdditionalServices = () => {
            if (!state.invoice) return

            const additional_services_values: any = state.invoice.additional_services_values,
                additional_services: any = { ...EMPTY_ADDITIONAL_SERVICES }

            for (const service_name in additional_services) {
                const key = ADDITIONAL_SERVICES_KEYS[service_name],
                    a_s_found = additional_services_values.find((a_s: any) => a_s.type_code === key)

                if (a_s_found) {
                    additional_services[service_name] = true
                }
            }

            return additional_services
        }

        const setCargo = () => {
            if (!state.invoice) return

            const volume_weight =
                (Number(state.invoice.width) *
                    Number(state.invoice.height) *
                    Number(state.invoice.depth)) /
                1000000

            const additional_services = getAdditionalServices()

            const declared_price = state.invoice.declared_price
                ? state.invoice.declared_price.toString()
                : '0'

            const cargo: CompletedCargoT = {
                receiver: state.invoice.receiver,
                logistics_info: {
                    additional_services,
                    product_name: state.invoice.product_name,
                    customer_invoice_number: state.invoice.dop_invoice_number,
                    weight: state.invoice.weight,
                    places: state.invoice.places,
                    depth: state.invoice.depth,
                    width: state.invoice.width,
                    height: state.invoice.height,
                    volume: volume_weight > 0.01 ? Number(volume_weight.toFixed(2)) : 0.01,
                    annotation: state.invoice.annotation,
                    is_return_documents: state.invoice.should_return_document,
                    cod_payment: state.invoice.cod_payment,
                    cargo_name: state.invoice.cargo_name,
                    declared_price,
                    shipment_type: state.invoice.shipment_type_id,
                    payment_type: state.invoice.payment_type_id,
                    payment_method: state.invoice.payment_method_id,
                    payer_company_id: state.invoice.payer_company_id,
                    cash_sum: state.invoice.cash_sum,
                    verify: state.invoice.verify,
                    is_dangerous: state.invoice.is_dangerous,
                    invoice_files: state.invoice.invoice_files,
                    certificate_of_safety_files: state.invoice.certificate_of_safety_files,
                    temperature_regime_type_id: state.invoice.temperature_regime
                        ? state.invoice.temperature_regime.id
                        : null,
                },
            }

            state.updating_cargo = cargo
        }

        const updateAdditionalServicesFromCargo = async (
            additional_services: AdditionalServicesI
        ) => {
            if (!state.invoice || !state.invoice.additional_services) return

            let payload: AdditionalServicesLTLI = {
                hasCar: false,
                carCost: 0,
                hasManipulator: false,
                manipulatorCost: 0,
                hasCrane: false,
                craneCost: 0,
                hasHydraulicTrolley: false,
                hydraulicTrolleyCost: 0,
                hasPallet: false,
                palletCost: 0,
                hasLoader: false,
                hoursOfLoadersWork: 0,
                amountOfLoaders: 0,
                hasSoftPackage: false,
                hasGrid: false,
            }

            if (state.additional_services_temp) {
                payload.hasCar = state.additional_services_temp.hasCar
                payload.carCost = state.additional_services_temp.carCost
                payload.hasManipulator = state.additional_services_temp.hasManipulator
                payload.manipulatorCost = state.additional_services_temp.manipulatorCost
                payload.hasCrane = state.additional_services_temp.hasCrane
                payload.craneCost = state.additional_services_temp.craneCost
                payload.hasHydraulicTrolley = state.additional_services_temp.hasHydraulicTrolley
                payload.hydraulicTrolleyCost = state.additional_services_temp.hydraulicTrolleyCost
                payload.hasPallet = state.additional_services_temp.hasPallet
                payload.palletCost = state.additional_services_temp.palletCost
                payload.hasLoader = state.additional_services_temp.hasLoader
                payload.hoursOfLoadersWork = state.additional_services_temp.hoursOfLoadersWork
                payload.amountOfLoaders = state.additional_services_temp.amountOfLoaders
                payload.hasSoftPackage = state.additional_services_temp.hasSoftPackage
                payload.hasGrid = state.additional_services_temp.hasGrid
            }

            payload.hasGrid = additional_services.hasGrid
            payload.hasPallet = additional_services.hasPallet
            payload.hasSoftPackage = additional_services.hasSoftPackage

            try {
                state.is_receiver_services_loading = true

                await receivers.updateAdditionalServices(
                    state.invoice.additional_services.id,
                    payload
                )
            } catch (error) {
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'При обновлении доп услуг произошла ошибка',
                })
            } finally {
                state.is_receiver_services_loading = false
            }
        }

        const onCargoUpdated = async ({
            additional_services,
        }: {
            additional_services: AdditionalServicesI
        }) => {
            try {
                await updateAdditionalServicesFromCargo(additional_services)
                router.go(-1)
            } catch (error) {
                console.error(error)
            }
        }

        const loadInvoice = async () => {
            try {
                state.is_loading = true

                const response = await orders.getInvoice(invoice_id)
                state.invoice = response.data
                setCargo()

                state.is_failed = false
            } catch (error) {
                state.is_failed = true
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'При загрузке данных накладной произошла ошибка',
                })
            } finally {
                state.is_loading = false
            }
        }

        onBeforeMount(loadInvoice)

        return {
            state,
            invoice_id,
            loadInvoice,
            onCargoUpdated,
        }
    },
})
